import { createPortal } from "react-dom";
import { Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@astryxdesign/core/Button";
import * as stylex from "@stylexjs/stylex";
import type { z } from "zod";
import { accessRequestSchema, governanceAPI } from "../lib/governance";
import { deadlineWarning, effectiveGrants, grantNow, remainingTime, ownsGrant, type WarningState } from "../lib/grant-time";
import { errorMessage } from "../lib/api";
import { styles } from "../lib/styles";
import { Drawer, FormGuard } from "./Workspace";
const extensionStyles = {
  compact: {
    kmVPX3: "xc7ga6q",
    k1K539: "x1yztbdb",
    $$css: true
  }
};
type Request = z.infer<typeof accessRequestSchema>;
const warningStates = new Map<string, WarningState>();
const extensionOutcomes = new Map<string, string>();
const outcomeBaselines = new Set<string>();
export function GrantDeadlines({
  requests,
  email,
  subject = "",
  compact = false,
  summarySlot = null
}: {
  requests: Request[];
  email: string;
  subject?: string;
  compact?: boolean;
  summarySlot?: HTMLDivElement | null;
}) {
  const [now, setNow] = useState(grantNow);
  const [alerts, setAlerts] = useState<Array<{
    key: string;
    message: string;
  }>>([]);
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [editing, setEditing] = useState<Request | null>(null);
  const [enabled, setEnabled] = useState(() => localStorage.getItem("ssbi-grant-alerts") === "1");
  const effective = effectiveGrants(requests, email, subject);
  const urgent = effective.filter(grant => Date.parse(grant.expires_at!) <= now + 300000).sort((a, b) => Date.parse(a.expires_at!) - Date.parse(b.expires_at!));
  const visible = compact ? urgent.slice(0, 2) : effective;
  useEffect(() => {
    const update = () => {
      const time = grantNow();
      setNow(time);
      const identity = subject || email;
      for (const request of requests) {
        if (!request.extends_request_id || !ownsGrant(request, email, subject)) continue;
        const key = identity + ":" + request.id;
        const previous = extensionOutcomes.get(key);
        if (previous !== request.status && outcomeBaselines.has(identity) && ["approved", "denied", "revoked"].includes(request.status)) {
          const message = request.status === "approved" && request.expires_at ? `Extension approved for ${request.server_id} until ${new Date(request.expires_at).toLocaleString()}. Device activation limits are unchanged.` : `Your extension for ${request.server_id} is ${request.status}. Access follows the remaining effective grants.`;
          setOutcomes(values => [message, ...values].slice(0, 3));
          if (enabled && "Notification" in window && Notification.permission === "granted") new Notification("SSBD Access", {
            body: message
          });
        }
        extensionOutcomes.set(key, request.status);
      }
      outcomeBaselines.add(identity);
      for (const grant of effectiveGrants(requests, email, subject)) {
        const deadline = Date.parse(grant.expires_at!);
        const key = `${subject || email}:${grant.server_id}:${deadline}`;
        const kind = deadlineWarning(key, deadline, time, warningStates);
        if (!kind) continue;
        const message = kind === "expired" ? `Access approval for ${grant.server_id} expired. Request fresh approval before reconnecting.` : `Access approval for ${grant.server_id} expires in ${kind === "one" ? "one minute or less" : "five minutes or less"}. Request more time if needed.`;
        setAlerts(previous => [{
          key,
          message
        }, ...previous.filter(alert => alert.key !== key)].slice(0, 4));
        if (enabled && "Notification" in window && Notification.permission === "granted") new Notification("SSBD Access", {
          body: message
        });
      }
    };
    const initial = setTimeout(update, 0);
    const timer = setInterval(update, 1000);
    return () => {
      clearTimeout(initial);
      clearInterval(timer);
    };
  }, [requests, email, subject, enabled]);
  const summary = (visible.length > 0 || outcomes.length > 0) && <section aria-label="Approval deadlines" data-workspace-panel {...stylex.props(styles.panel, compact && extensionStyles.compact)}>
      {!compact && <h2 {...stylex.props(styles.panelHeading)}>Approval deadlines</h2>}
      {outcomes.map(message => <output data-governance-notice {...stylex.props(styles.notice)} key={message}>
          {message}
        </output>)}
      {!compact && <p {...stylex.props(styles.small)}>
          The longest effective approval for each server determines this countdown. An extension
          requires review; your device activation limit is separate.
        </p>}
      {!compact && alerts.filter(alert => effective.some(grant => alert.key === `${subject || email}:${grant.server_id}:${Date.parse(grant.expires_at!)}`)).map(({
      message,
      key
    }) => <p role="alert" {...stylex.props(styles.notice)} key={key}>
              {message}
            </p>)}
      {visible.map(grant => {
      const pending = requests.find(request => request.extends_request_id === grant.id && request.status === "pending");
      return <div className={compact ? undefined : "list-row"} {...{
        0: {},
        1: {
          className: "x78zum5 x6s0dn4 x1qughib x1v2ro7d x1a02dak"
        }
      }[!!compact << 0]} key={grant.server_id}>
            <div>
              <strong>{grant.server_id}</strong>
              <p>
                <span aria-label={`Time remaining for ${grant.server_id}`}>
                  {remainingTime(Date.parse(grant.expires_at!), now)}
                </span>{" "}
                {!compact && <>· {new Date(grant.expires_at!).toLocaleString()}</>}
              </p>
              {compact && alerts.filter(alert => alert.key === `${subject || email}:${grant.server_id}:${Date.parse(grant.expires_at!)}`).map(alert => <p role="alert" key={alert.key} {...stylex.props(styles.small)}>
                      {alert.message}
                    </p>)}
              {pending && <p>
                  {Date.parse(grant.expires_at!) > now ? "Extension awaiting review. Current expiry is unchanged." : "Extension source expired. Submit a fresh access request; this extension cannot be approved."}
                </p>}
            </div>
            {Date.parse(grant.expires_at!) > now && <Button label="Request more time" aria-label={`Request more time for ${grant.server_id}`} isDisabled={!!pending} onClick={() => setEditing(grant)} />}
          </div>;
    })}
      {compact && <Link to="/requests">
          {urgent.length > 2 ? `${urgent.length - 2} more expiring approvals · ` : ""}View all
          requests
        </Link>}
      {!compact && "Notification" in window && <Button label={enabled ? "Disable browser expiry alerts" : "Enable browser expiry alerts"} size="sm" onClick={() => {
      if (enabled) {
        localStorage.removeItem("ssbi-grant-alerts");
        setEnabled(false);
        return;
      }
      void Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          localStorage.setItem("ssbi-grant-alerts", "1");
          setEnabled(true);
        }
      });
    }} />}
    </section>;
  return <>
      {compact ? summary : summarySlot ? createPortal(summary, summarySlot) : null}
      {editing && <Drawer title="Request more time" description={`Current approval for ${editing.server_id} expires ${new Date(editing.expires_at!).toLocaleString()}. It stays unchanged while this request is reviewed.`} onClose={() => setEditing(null)}>
          <ExtensionForm grant={editing} onSaved={() => setEditing(null)} />
        </Drawer>}
    </>;
}
function ExtensionForm({
  grant,
  onSaved
}: {
  grant: Request;
  onSaved: () => void;
}) {
  const [minutes, setMinutes] = useState(15);
  const [reason, setReason] = useState("");
  const cache = useQueryClient();
  const save = useMutation({
    mutationFn: () => governanceAPI.extend(grant.id, minutes, reason.trim()),
    onSuccess: async () => {
      await cache.invalidateQueries({
        queryKey: ["governance"]
      });
      onSaved();
    }
  });
  return <form className="xrvj5dj x1mkdm3x x1c1vhfx" onSubmit={event => {
    event.preventDefault();
    save.mutate();
  }}>
      <FormGuard dirty={!!reason || minutes !== 15} busy={save.isPending} />
      <label {...stylex.props(styles.field)}>
        Additional minutes
        <input {...stylex.props(styles.select)} type="number" min={1} max={480} value={minutes} onChange={event => setMinutes(Number(event.target.value))} required />
      </label>
      <label {...stylex.props(styles.field)}>
        Extension reason
        <input {...stylex.props(styles.select)} value={reason} onChange={event => setReason(event.target.value)} required minLength={5} maxLength={1000} />
      </label>
      <p>An approver must review this request. Your current expiry stays unchanged.</p>
      {save.error && <p role="alert">
          {errorMessage(save.error)} Your entries have been kept. Retry after resolving the issue.
        </p>}
      <Button label="Submit extension request" variant="primary" type="submit" isLoading={save.isPending} />
    </form>;
}